import { useState, useEffect, useRef } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useBlockingCount } from '../hooks/useBlockingTracker';
import BrandSpinner from './BrandSpinner';
import './LoadingOverlay.css';

const MIN_VISIBLE  = 600;  // ms spinner must stay visible once shown (prevents flash)
const MUTATE_DELAY = 100;  // ms for mutations (user-triggered, respond faster)

export default function LoadingOverlay() {
  // Exclude background mutations tagged with meta: { background: true }.
  const isMutating = useIsMutating({ predicate: m => !m.options.meta?.background });
  // Plain-fetch calls that opted in via useTrackAsync().
  const blockingCount = useBlockingCount();
  const [visible, setVisible] = useState(false);

  const showTimer = useRef(null);
  const hideTimer = useRef(null);
  const shownAt   = useRef(null);

  useEffect(() => {
    const isActive = isMutating > 0 || blockingCount > 0;
    const delay    = MUTATE_DELAY;

    if (isActive) {
      clearTimeout(hideTimer.current);
      if (!visible) {
        showTimer.current = setTimeout(() => {
          setVisible(true);
          shownAt.current = Date.now();
        }, delay);
      }
    } else {
      clearTimeout(showTimer.current);
      if (visible) {
        const elapsed   = Date.now() - (shownAt.current ?? Date.now());
        const remaining = Math.max(0, MIN_VISIBLE - elapsed);
        hideTimer.current = setTimeout(() => setVisible(false), remaining);
      }
    }

    return () => {
      clearTimeout(showTimer.current);
      clearTimeout(hideTimer.current);
    };
  }, [isMutating, blockingCount, visible]);

  // Set aria-busy on the app root so screen readers know the page is busy.
  useEffect(() => {
    document.getElementById('root')?.setAttribute('aria-busy', String(visible));
    return () => document.getElementById('root')?.setAttribute('aria-busy', 'false');
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="loading-overlay" role="status" aria-live="polite" aria-label="Loading, please wait">
      <div className="loading-box">
        <BrandSpinner size={96} />
        <span className="loading-text">Loading…</span>
      </div>
    </div>
  );
}
